import { useState } from "react"
import { useEffect } from "react"
import { BtcMarkets } from "../../../api/BtcMarkets"
import { QueryOrderResult } from "../../../interface/QueryOrderResult"
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';

import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { Transition } from "../../../animations/Transition";
import { MarketChart } from "../../charts/MarketChart";
import moment from 'moment';


interface OrderDetailsModalProps
{
  open: boolean
  orderId: number
  marketId: string
  eventTimestamp?: Date
  eventPrice?: number
  eventSide?: "BUY" | "SELL"
  handleClose: () => void
}


export const OrderDetailsModal = ({ open, handleClose, orderId, marketId, eventTimestamp, eventPrice, eventSide }: OrderDetailsModalProps) =>
{
  const theme = useTheme()
  const [ orderDetails, setOrderDetails ] = useState<QueryOrderResult | undefined>(undefined)
  const [ loading, setLoading ] = useState(false)
  useEffect(() => {

    getOrderDetails()

  }, [])

  const getOrderDetails = async () =>
  {
    try
    {
      setLoading(true)
      const response = await new BtcMarkets().getOrder(marketId, orderId)
      setOrderDetails(response)
      setLoading(false)
    }
    catch(error)
    {
      setLoading(false)
    }
  }

  const getDisplayExecutionPrice = () => {
    const orderPrice = Number(orderDetails?.price)
    if(Number.isFinite(orderPrice) && orderPrice > 0) return orderPrice
    if(typeof eventPrice === "number") return eventPrice
    return undefined
  }

  const executionTimestampValue = orderDetails?.time || orderDetails?.updateTime || (eventTimestamp ? new Date(eventTimestamp).getTime() : undefined)
  const executionSide = (orderDetails?.side?.toUpperCase() as "BUY" | "SELL" | undefined) || eventSide
  const candleFromTime = executionTimestampValue
    ? moment(executionTimestampValue).subtract(10, 'days').toISOString()
    : undefined
  const detailRows = orderDetails
    ? Object.entries(orderDetails).map(([key, value]) => ({ key, value }))
    : []


  return(
    <Dialog
    open={open}
    TransitionComponent={Transition}
    keepMounted
    fullWidth
    maxWidth="lg"
    PaperProps={{
      sx: {
        borderRadius: "18px",
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
            : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
      }
    }}
    onClose={handleClose}
    aria-describedby="alert-dialog-slide-description">
  <DialogTitle sx={{ px: 3, pt: 3, pb: 2, borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}` }}>
    <Grid container spacing={2} alignItems="center">
      <Grid item>
        <Box sx={styles.headerIcon(theme)}>
          <ReceiptLongRoundedIcon />
        </Box>
      </Grid>
      <Grid item xs>
        <Typography variant="overline" sx={{ letterSpacing: 1.5, color: alpha(theme.palette.text.primary, 0.64) }}>
          Order Inspection
        </Typography>
        <Typography variant="h5">Order Details</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: alpha(theme.palette.text.primary, 0.68) }}>
          Review the exchange response and see where the order executed on the chart.
        </Typography>
      </Grid>
      <Grid item>
        <IconButton onClick={handleClose} sx={styles.closeButton(theme)}>
          <CloseIcon />
        </IconButton>
      </Grid>
    </Grid>
  </DialogTitle>
  <DialogContent sx={{ px: 3, py: 2.5 }}>
    {loading ? (
      <Box sx={styles.spinner}><CircularProgress /></Box>
    ) : (
      <Stack spacing={2.5}>
        <Box sx={styles.hero(theme)}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={styles.sectionLabel(theme)}>Execution Summary</Typography>
              <Typography variant="h5" sx={{ mt: 0.5 }}>
                {marketId} {executionSide ? executionSide.toLowerCase() : "order"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: alpha(theme.palette.text.primary, 0.68) }}>
                {executionTimestampValue ? `Executed ${moment(executionTimestampValue).format("lll")} (${moment(executionTimestampValue).fromNow()})` : "Execution time unavailable"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.25} alignItems={{ xs: "flex-start", md: "stretch" }}>
                <Chip label={`Order ID ${orderId}`} sx={styles.summaryChip(theme)} />
                <Chip label={`Status ${orderDetails?.status || "unknown"}`} sx={styles.summaryChip(theme)} />
                <Chip label={`Price ${getDisplayExecutionPrice() ? `$${getDisplayExecutionPrice()}` : "market"}`} sx={styles.summaryChip(theme)} />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={styles.sectionCard(theme)}>
          <Typography variant="overline" sx={styles.sectionLabel(theme)}>Execution Chart</Typography>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Where the order executed</Typography>
          <MarketChart
            height={420}
            tradingPair={marketId}
            chartInterval="15m"
            candleFromTime={candleFromTime}
            executionTimestamp={executionTimestampValue}
            executionPrice={getDisplayExecutionPrice()}
            executionType={executionSide}
            currentPrice={getDisplayExecutionPrice()}
          />
        </Box>

        <Box sx={styles.sectionCard(theme)}>
          <Typography variant="overline" sx={styles.sectionLabel(theme)}>Exchange Payload</Typography>
          <Grid container spacing={1.5}>
            {detailRows.map(({ key, value }) => (
              <Grid item xs={12} md={6} key={key}>
                <Box sx={styles.detailItem(theme)}>
                  <Typography variant="caption" sx={{ color: alpha(theme.palette.text.primary, 0.62), textTransform: "uppercase", letterSpacing: 1.1 }}>
                    {key}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, wordBreak: "break-word" }}>
                    {String(value)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    )}
  </DialogContent>
  <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`, backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02) }}>
    <Button onClick={handleClose} variant="contained" sx={{ minWidth: 120, borderRadius: "12px", boxShadow: "none" }}>Close</Button>
  </DialogActions>
</Dialog>
  )
}

const styles = {
  spinner: {
    flex: 1,
    display: 'flex' as 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  headerIcon: (theme: any) => ({
    width: 44,
    height: 44,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  closeButton: (theme: any) => ({
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  hero: (theme: any) => ({
    p: 2.5,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#102946", 0.7)} 0%, ${alpha("#0c1727", 0.85)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.9)} 0%, ${alpha("#e8f2ff", 0.9)} 100%)`,
  }),
  sectionCard: (theme: any) => ({
    p: 2,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.58),
  }),
  sectionLabel: (theme: any) => ({
    display: "block",
    mb: 0.5,
    letterSpacing: 1.4,
    color: alpha(theme.palette.text.primary, 0.62),
  }),
  summaryChip: (theme: any) => ({
    justifyContent: "flex-start",
    borderRadius: "999px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.72),
    color: theme.palette.text.primary,
  }),
  detailItem: (theme: any) => ({
    p: 1.5,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.4)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.02 : 0.7),
    minHeight: 84,
  }),
}
